/**
 * CDN prefix for image transformation via Cloudflare Image Resizing.
 * In production the path goes through `/cdn-cgi/image/` which lets
 * @unpic/svelte generate responsive srcset URLs with on-the-fly resizing.
 * In dev mode the prefix is empty so images load from local `static/`.
 */
export const cdnUrl = import.meta.env.DEV ? "" : "https://overwatchbrain.com/cdn-cgi/image/";

/** Get the (optionally CDN-prefixed) path for a hero's portrait image. */
export function getPortrait(slug: string): string {
    return `${cdnUrl}/images/portraits/${slug}.png`;
}

/** Get the (optionally CDN-prefixed) path for a hero's header/banner image. */
export function getHeader(slug: string, ext: string): string {
    return `${cdnUrl}/images/headers/${slug}.${ext || "png"}`;
}

/**
 * Get a Cloudflare-transformed header URL with width-capped resizing,
 * format conversion, and quality optimization for above-the-fold banners.
 * In dev mode returns the raw static path.
 */
export function getHeaderRaw(slug: string, ext: string): string {
    if (import.meta.env.DEV) {
        return `/images/headers/${slug}.${ext || "png"}`;
    }
    return `https://overwatchbrain.com/cdn-cgi/image/format=auto,quality=90,width=1280/images/headers/${slug}.${ext || "png"}`;
}

/** Responsive srcset for hero banner images via Cloudflare Image Resizing. */
export function getHeaderSrcset(slug: string, ext: string): string {
    if (import.meta.env.DEV) return "";
    const path = `/images/headers/${slug}.${ext || "png"}`;
    return [640, 960, 1280]
        .map((w) => `https://overwatchbrain.com/cdn-cgi/image/format=auto,quality=90,width=${w}${path} ${w}w`)
        .join(", ");
}

/** Get the (optionally CDN-prefixed) path for a showcase image. */
export function getShowcase(path: string): string {
    return `${cdnUrl}${path}`;
}

/** Get the local path for a role icon SVG. */
export function getRoleIcon(role: string): string {
    return `/images/icons/role-${role}.svg`;
}

/** Map of competitive rank tier key → icon filename slug. */
const RANK_ICON_SLUG: Record<string, string> = {
    Bronze: "bronze",
    Silver: "silver",
    Gold: "gold",
    Platinum: "platinum",
    Diamond: "diamond",
    Master: "master",
    Grandmaster: "grandmaster",
    Champion: "champion",
};

/**
 * Get the local path for a competitive rank badge icon.
 * Returns `null` for the "All" tier or any unrecognised key.
 * Handles "+" tier keys (e.g. "Silver+") by using the base rank's icon.
 */
export function getRankIcon(tier: string): string | null {
    const slug = RANK_ICON_SLUG[tier] ?? RANK_ICON_SLUG[tier.endsWith("+") ? tier.slice(0, -1) : tier];
    return slug ? `/images/icons/rank-${slug}.png` : null;
}

/** Get the (optionally CDN-prefixed) path for a map thumbnail image. */
export function getMapImage(mapKey: string): string {
    return `${cdnUrl}/images/maps/${mapKey}.jpg`;
}

/**
 * Get a Cloudflare-transformed map image URL with width-capped resizing
 * for above-the-fold banners. In dev mode returns the raw static path.
 */
export function getMapImageRaw(mapKey: string): string {
    if (import.meta.env.DEV) {
        return `/images/maps/${mapKey}.jpg`;
    }
    return `https://overwatchbrain.com/cdn-cgi/image/format=auto,quality=80,width=1280/images/maps/${mapKey}.jpg`;
}

/** Responsive srcset for map banner images via Cloudflare Image Resizing. */
export function getMapImageSrcset(mapKey: string): string {
    if (import.meta.env.DEV) return "";
    const path = `/images/maps/${mapKey}.jpg`;
    return [640, 960, 1280]
        .map((w) => `https://overwatchbrain.com/cdn-cgi/image/format=auto,quality=80,width=${w}${path} ${w}w`)
        .join(", ");
}

/**
 * Get a small, optimized map image for decorative table-row backgrounds.
 * Keeps bandwidth minimal for the many rows in per-hero map tables.
 */
export function getMapImageThumb(mapKey: string): string {
    if (import.meta.env.DEV) {
        return `/images/maps/${mapKey}.jpg`;
    }
    return `https://overwatchbrain.com/cdn-cgi/image/format=auto,quality=60,width=400/images/maps/${mapKey}.jpg`;
}
